import React, { useState } from 'react';

import { Box, SxProps, Theme, useTheme } from '@mui/material';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
  type UniqueIdentifier,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { IconGripVertical } from 'src/libraries/icons';

//
//

export type AppSortableItem = {
  id: UniqueIdentifier;
};

export type AppSortableListV3Props<T extends AppSortableItem> = {
  items: T[];
  onReorder: (items: T[]) => void;
  renderItem: (item: T, index: number, isDragging: boolean) => React.ReactNode;
  onDraggingChange?: (isDragging: boolean) => void;
  squeezeOnDrag?: boolean;
  disabled?: boolean;
  gap?: number;
  sx?: SxProps<Theme>;
  itemSx?: SxProps<Theme>;
};

type SortableRowProps = {
  id: UniqueIdentifier;
  disabled?: boolean;
  squeezeOnDrag?: boolean;
  sx?: SxProps<Theme>;
  children: React.ReactNode;
};

const SQUEEZED_MAX_HEIGHT = 56;
const EXPANDED_MAX_HEIGHT = 2000;

const SortableRow: React.FC<SortableRowProps> = ({ id, disabled, squeezeOnDrag, sx, children }) => {
  const { setNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable({
    id,
    disabled,
  });

  return (
    <Box
      ref={setNodeRef}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        // gap: 1,
        background: (theme) => theme.palette.background.paper,
        transform: CSS.Transform.toString(transform),
        ...(squeezeOnDrag
          ? {
              maxHeight: isDragging ? SQUEEZED_MAX_HEIGHT : EXPANDED_MAX_HEIGHT,
              overflow: 'hidden',
              opacity: isDragging ? 0.5 : 1,
              transition: [transition, 'max-height 200ms ease, opacity 200ms ease']
                .filter(Boolean)
                .join(', '),
            }
          : {
              opacity: isDragging ? 0 : 1,
              transition,
            }),
        ...sx,
      }}
    >
      {!disabled && (
        <Box
          {...attributes}
          {...listeners}
          role="button"
          aria-label="Drag to reorder"
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: 'text.secondary',
            cursor: 'grab',
            touchAction: 'none',
            px: 1.5,
            pr: 0,
            py: 2.5,
            height: 62,
          }}
        >
          <IconGripVertical size={16} />
        </Box>
      )}

      <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
    </Box>
  );
};

type DragOverlayRowProps = {
  sx?: SxProps<Theme>;
  children: React.ReactNode;
};

const DragOverlayRow: React.FC<DragOverlayRowProps> = ({ sx, children }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        cursor: 'grabbing',
        border: '1px solid transparent',
        ...sx,
        background: theme.palette.background.paper,
        borderColor: theme.palette.primary.main,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          color: 'text.secondary',
          px: 1.5,
          pr: 0,
          py: 2.5,
          height: 62,
        }}
      >
        <IconGripVertical size={16} />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
    </Box>
  );
};

export function AppSortableList<T extends AppSortableItem>({
  items,
  onReorder,
  renderItem,
  onDraggingChange,
  squeezeOnDrag = false,
  disabled = false,
  gap = 2,
  sx,
  itemSx,
}: AppSortableListV3Props<T>) {
  const [isDragging, setIsDragging] = useState(false);
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragStart = (event: DragStartEvent) => {
    setIsDragging(true);
    setActiveId(event.active.id);
    onDraggingChange?.(true);
  };

  const resetDragState = () => {
    setIsDragging(false);
    setActiveId(null);
    onDraggingChange?.(false);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    resetDragState();

    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex((item) => item.id === active.id);
    const newIndex = items.findIndex((item) => item.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    onReorder(arrayMove(items, oldIndex, newIndex));
  };

  const activeIndex = items.findIndex((item) => item.id === activeId);
  const activeItem = activeIndex !== -1 ? items[activeIndex] : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={resetDragState}
    >
      <SortableContext items={items.map((item) => item.id)} strategy={verticalListSortingStrategy}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap, ...sx }}>
          {items.map((item, index) => (
            <SortableRow
              key={item.id}
              id={item.id}
              disabled={disabled}
              squeezeOnDrag={squeezeOnDrag}
              sx={itemSx}
            >
              {renderItem(item, index, isDragging)}
            </SortableRow>
          ))}
        </Box>
      </SortableContext>

      <DragOverlay>
        {activeItem && (
          <DragOverlayRow sx={itemSx}>{renderItem(activeItem, activeIndex, true)}</DragOverlayRow>
        )}
      </DragOverlay>
    </DndContext>
  );
}
